<template>
  <div class="oai-Flow">
    <OnboardingAiStepper
      v-if="!hasLayoutStepper && !isStepperHidden"
      :current-step="currentStepIndex"
      :total-count="stepResolver.totalStepperSteps"
      class="oai-Flow_Stepper"
    />

    <div class="oai-Flow_Body">
      <Transition mode="out-in" name="slide-next">
        <component
          :is="activeStepBind?.component"
          v-if="activeStepBind"
          :key="activeStepBind.key"
          v-bind="activeStepBind.props"
          v-on="activeStepBind.events"
        />
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { isNull, isObject, isUndefined } from 'lodash'
import { computed, nextTick, onMounted, onUnmounted, ref, toValue, watch } from 'vue'
import { useStore } from 'vuex'

import OnboardingAiApiHandler from '@/api/onboarding-ai'
import {
  useOnboardingAiStepResolver,
  DEFAULT_STRUCTURE_OPTION_ID,
  ONBOARDING_DATA_KEY,
  ONBOARDING_DATA_TYPE,
  ONBOARDING_DATA_TYPE_TO_STRING_MAPPER,
  ONBOARDING_STATE,
  ONBOARDING_STEP,
  ONBOARDING_TRACKING_STEP_EVENTS,
  provideOnboardingAiBillingComplete,
  REDIRECT_STEP_MAPPER,
  useOnboardingAiStepContext,
  useOnboardingAiTracking,
  useSyncOnboardingAiStepContext
} from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { handleError } from '@/utils/error-handling'
import { resolveCompanyWebsiteFromEmail } from '@/utils/onboarding-ai/company-website-from-email'
import { getDefaultOnboardingAiIntervalId } from '@/utils/onboarding-ai/interval-options'
import {
  findOnboardingDataByType,
  getLoadingConfigForStep,
  getOnboardingDataValue,
  isOnboardingStateFailed,
  isRequestFailed,
  isRequestPending,
  tryParseJsonValue
} from '@/utils/onboarding-ai/loading-mapper'
import {
  getRecommendedStructureIdFromCompanySize,
  resolveSelectedStructureId
} from '@/utils/onboarding-ai/structure-recommendation'
import { useReloadPage } from '@/utils/reload-page'
import { ONBOARDING_HUB, USER_SETTINGS_MAPPER } from '@/utils/user-settings'
import { clearLastSelectedWorkspace } from '@/utils/workspaces'

import OnboardingAiStepper from '@/components/ui/onboarding-ai/OnboardingAiStepper'
import OnboardingAiBillingStep from '@/components/ui/onboarding-ai/steps/OnboardingAiBillingStep'
import OnboardingAiBookDemoStep from '@/components/ui/onboarding-ai/steps/OnboardingAiBookDemoStep'
import OnboardingAiFailedStep from '@/components/ui/onboarding-ai/steps/OnboardingAiFailedStep'
import OnboardingAiGoalsStep from '@/components/ui/onboarding-ai/steps/OnboardingAiGoalsStep'
import OnboardingAiIntroStep from '@/components/ui/onboarding-ai/steps/OnboardingAiIntroStep'
import OnboardingAiInviteStep from '@/components/ui/onboarding-ai/steps/OnboardingAiInviteStep'
import OnboardingAiLoadingStep from '@/components/ui/onboarding-ai/steps/OnboardingAiLoadingStep'
import OnboardingAiOkrIntervalStep from '@/components/ui/onboarding-ai/steps/OnboardingAiOkrIntervalStep'
import OnboardingAiOkrStructureStep from '@/components/ui/onboarding-ai/steps/OnboardingAiOkrStructureStep'
import OnboardingAiResearchReviewStep from '@/components/ui/onboarding-ai/steps/OnboardingAiResearchReviewStep'
import OnboardingAiWebsiteStep from '@/components/ui/onboarding-ai/steps/OnboardingAiWebsiteStep'

defineOptions({
  name: 'OnboardingAiFlow'
})

const { reloadPage } = useReloadPage()

const store = useStore()

const { logEvent } = useOnboardingAiTracking()

const stepResolver = useOnboardingAiStepResolver()

const localData = ref({
  currentStep: null,
  [ONBOARDING_DATA_KEY.COMPANY_WEBSITE]: '',
  [ONBOARDING_DATA_KEY.SELECTED_GOALS]: [],

  [ONBOARDING_DATA_KEY.RECOMMENDED_STRUCTURE_ID]: null,
  [ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID]: DEFAULT_STRUCTURE_OPTION_ID,

  [ONBOARDING_DATA_KEY.RESEARCH_DATA]: null,
  [ONBOARDING_DATA_KEY.RESEARCH_NOTES]: '',
  [ONBOARDING_DATA_KEY.SELECTED_INTERVAL_ID]: getDefaultOnboardingAiIntervalId()
})

const isCurrentRequestLoading = ref(false)
const isNavigating = ref(false)

const stepContext = useOnboardingAiStepContext()

const REQUEST_POLL_INTERVAL_MS = 1000

let requestPollTimerId = null
let isRequestPollActive = false

const currentLoadingConfig = computed(() => getLoadingConfigForStep(localData.value.currentStep))

const clearRequestPoll = () => {
  isRequestPollActive = false

  if (requestPollTimerId) {
    clearTimeout(requestPollTimerId)
    requestPollTimerId = null
  }
}

const scheduleNextPoll = dataTypeId => {
  requestPollTimerId = setTimeout(() => pollRequest(dataTypeId), REQUEST_POLL_INTERVAL_MS)
}

const transitionToFailedStep = () => {
  clearRequestPoll()
  isCurrentRequestLoading.value = false
  localData.value.currentStep = ONBOARDING_STEP.FAILED_LOADING

  const researchRequestFailed = findOnboardingDataByType(
    state.value.data,
    ONBOARDING_DATA_TYPE.RESEARCH_REQUEST
  )
  if (researchRequestFailed && isRequestFailed(researchRequestFailed.statusId)) {
    logEvent(EVENT_NAMES.ONBOARDING_FAILED, {
      request: ONBOARDING_DATA_TYPE_TO_STRING_MAPPER[ONBOARDING_DATA_TYPE.RESEARCH_REQUEST]
    })
  }
}

const handleOnboardingFailedFromPoll = () => {
  if (localData.value.currentStep === ONBOARDING_STEP.FAILED_LOADING) {
    return
  }

  transitionToFailedStep()
}

const pollRequest = async dataTypeId => {
  try {
    await pullOnboardingState()

    if (!isRequestPollActive) {
      return
    }

    if (isOnboardingStateFailed(state.value.stateId)) {
      handleOnboardingFailedFromPoll()
      return
    }

    const request = findOnboardingDataByType(state.value.data, dataTypeId)

    if (!request) {
      isCurrentRequestLoading.value = false
      clearRequestPoll()
      return
    }

    if (isRequestPending(request.statusId)) {
      isCurrentRequestLoading.value = true
      scheduleNextPoll(dataTypeId)
      return
    }

    if (isRequestFailed(request.statusId)) {
      handleOnboardingFailedFromPoll()
      return
    }

    applyLoadingRequestResult(dataTypeId, request)

    isCurrentRequestLoading.value = false
    clearRequestPoll()
  } catch (error) {
    handleError({ error })

    if (isRequestPollActive) {
      scheduleNextPoll(dataTypeId)
    }
  }
}

const startRequestPoll = dataTypeId => {
  clearRequestPoll()
  isRequestPollActive = true
  isCurrentRequestLoading.value = true
  pollRequest(dataTypeId)
}

const onLoadingStepComplete = () => {
  clearRequestPoll()
  goNext()
}

const onFailedStepComplete = () => {
  reloadPage()
}

const currentStepIndex = computed(() => stepResolver.getStepperIndex(localData.value.currentStep))

const isStepperHidden = computed(() => stepResolver.isStepperHidden(localData.value.currentStep))

const hasLayoutStepper = Boolean(
  useSyncOnboardingAiStepContext(
    currentStepIndex,
    stepResolver,
    computed(() => localData.value.currentStep)
  )
)

const wizardStepNumber = computed(() =>
  stepResolver.getWizardStepNumber(localData.value.currentStep)
)

const createWizardStepBind = (component, dataKey, { extraProps = {}, extraEvents = {} } = {}) => ({
  component,
  props: {
    modelValue: localData.value[dataKey],
    wizardStep: wizardStepNumber.value,
    ...extraProps
  },
  events: {
    'update:modelValue': value => {
      localData.value[dataKey] = value
    },
    next: goNext,
    ...extraEvents
  }
})

const STEP_BIND_BUILDERS = {
  [ONBOARDING_STEP.INTRO]: () => ({
    component: OnboardingAiIntroStep,
    props: {},
    events: { next: goNext }
  }),
  [ONBOARDING_STEP.WEBSITE]: () =>
    createWizardStepBind(OnboardingAiWebsiteStep, ONBOARDING_DATA_KEY.COMPANY_WEBSITE),
  [ONBOARDING_STEP.GOALS]: () =>
    createWizardStepBind(OnboardingAiGoalsStep, ONBOARDING_DATA_KEY.SELECTED_GOALS),
  [ONBOARDING_STEP.STRUCTURE]: () =>
    createWizardStepBind(OnboardingAiOkrStructureStep, ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID, {
      extraProps: {
        recommendedStructureId: localData.value[ONBOARDING_DATA_KEY.RECOMMENDED_STRUCTURE_ID]
      }
    }),
  [ONBOARDING_STEP.INTERVAL]: () =>
    createWizardStepBind(OnboardingAiOkrIntervalStep, ONBOARDING_DATA_KEY.SELECTED_INTERVAL_ID),
  [ONBOARDING_STEP.REVIEW]: () =>
    createWizardStepBind(OnboardingAiResearchReviewStep, ONBOARDING_DATA_KEY.RESEARCH_NOTES, {
      extraProps: {
        researchData: localData.value[ONBOARDING_DATA_KEY.RESEARCH_DATA],
        selectedGoals: localData.value[ONBOARDING_DATA_KEY.SELECTED_GOALS],
        selectedStructureId: localData.value[ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID]
      }
    }),
  [ONBOARDING_STEP.INVITE]: () => ({
    component: OnboardingAiInviteStep,
    props: { wizardStep: wizardStepNumber.value },
    events: { next: goNext }
  }),
  [ONBOARDING_STEP.BILLING]: () => ({
    component: OnboardingAiBillingStep,
    props: {},
    events: {}
  }),
  [ONBOARDING_STEP.BOOK_DEMO]: () => ({
    component: OnboardingAiBookDemoStep,
    props: { wizardStep: wizardStepNumber.value },
    events: { next: finishOnboarding }
  })
}

const activeStepBind = computed(() => {
  const step = localData.value.currentStep

  if (step === ONBOARDING_STEP.FAILED_LOADING) {
    return {
      component: OnboardingAiFailedStep,
      key: step,
      props: {},
      events: { complete: onFailedStepComplete }
    }
  }

  const loadingConfig = currentLoadingConfig.value

  if (loadingConfig) {
    return {
      component: OnboardingAiLoadingStep,
      key: step,
      props: {
        loading: isCurrentRequestLoading.value,
        variant: loadingConfig.variant
      },
      events: { complete: onLoadingStepComplete }
    }
  }

  const buildStepBind = STEP_BIND_BUILDERS[step]

  if (!buildStepBind) {
    return null
  }

  return {
    key: step,
    ...buildStepBind()
  }
})

/**
 * See return type of getOnboarding api method in OnboardingAiApiHandler:
 * {
 *   id: any,
 *   stateId: any,
 *   data: Array<{ dataTypeId: any, value: any, statusId: any }>
 * }
 *
 * Each request in all "apply request result" functions will be of type:
 *   { dataTypeId: any, value: any, statusId: any }
 *
 * This return type is important for:
 *   - applyCompanySizeRequestResult(request)
 *   - applyResearchRequestResult(request)
 */

const applyCompanySizeRequestResult = request => {
  if (!isNull(request?.value)) {
    localData.value[ONBOARDING_DATA_KEY.RECOMMENDED_STRUCTURE_ID] =
      getRecommendedStructureIdFromCompanySize(request.value)
  }

  localData.value[ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID] = resolveSelectedStructureId({
    recommendedStructureId: localData.value[ONBOARDING_DATA_KEY.RECOMMENDED_STRUCTURE_ID]
  })
}

const applyResearchRequestResult = request => {
  if (!isNull(request?.value)) {
    localData.value[ONBOARDING_DATA_KEY.RESEARCH_DATA] =
      tryParseJsonValue(request.value) ?? request.value
  }
}

const LOADING_REQUEST_RESULT_APPLIERS = {
  [ONBOARDING_DATA_TYPE.COMPANY_SIZE_REQUEST]: applyCompanySizeRequestResult,
  [ONBOARDING_DATA_TYPE.RESEARCH_REQUEST]: applyResearchRequestResult
}

const applyLoadingRequestResult = (dataTypeId, request) => {
  LOADING_REQUEST_RESULT_APPLIERS[dataTypeId]?.(request)
}

const resolveNextStep = async currentStep => {
  let nextStep = stepResolver.getNextStep(currentStep)

  while (nextStep) {
    const loadingConfig = getLoadingConfigForStep(nextStep)

    if (!loadingConfig) {
      return nextStep
    }

    // NOTE: Research loading is not skipped even if the request is not pending
    if (nextStep === ONBOARDING_STEP.RESEARCH_LOADING) {
      return nextStep
    }

    try {
      await pullOnboardingState()

      const request = findOnboardingDataByType(state.value.data, loadingConfig.dataTypeId)

      if (request && !isRequestPending(request.statusId)) {
        applyLoadingRequestResult(loadingConfig.dataTypeId, request)
        nextStep = stepResolver.getNextStep(nextStep)
        continue
      }
    } catch (error) {
      handleError({ error })
    }

    return nextStep
  }

  return nextStep
}

const pullOnboardingState = async () => {
  const onboarding = await api.getOnboarding()
  state.value = onboarding
  return onboarding
}

const isOkToUpdate = async () => {
  try {
    await pullOnboardingState()

    if (isOnboardingStateFailed(state.value.stateId)) {
      transitionToFailedStep()
      return false
    }

    return true
  } catch (error) {
    handleError({ error })
    return false
  }
}

const trackStepEvent = (payload = {}) => {
  if (!isUndefined(ONBOARDING_TRACKING_STEP_EVENTS[localData.value.currentStep])) {
    logEvent(ONBOARDING_TRACKING_STEP_EVENTS[localData.value.currentStep], {
      ...payload
    })
  }
}

const syncRemoteState = async (stateId, { payload = {}, callback = () => {} }) => {
  if (!state.value?.id) {
    return false
  }

  if (!stepResolver.isBackendState(stateId)) {
    trackStepEvent(payload)
    return true
  }

  const okToUpdate = await isOkToUpdate()
  if (!okToUpdate) {
    return false
  }

  try {
    await api.updateOnboardingState({
      id: state.value.id,
      stateId
    })

    trackStepEvent(payload)
    callback?.()

    state.value.stateId = stateId
    return true
  } catch (error) {
    handleError({ error })
    return false
  }
}

const setNavigating = value => {
  isNavigating.value = value

  if (stepContext) {
    stepContext.navigating = value
  }
}

const completeOnboarding = async (options = {}) => {
  const synced = await syncRemoteState(ONBOARDING_STATE.COMPLETED, options)

  if (!synced) {
    return
  }

  try {
    await markAdminOnboardingPassed()
  } finally {
    clearLastSelectedWorkspace()
    reloadPage()
  }
}

const markAdminOnboardingPassed = async () => {
  const oldState = store.getters['system/onboardingHubState']
  const newState = {
    ...oldState,
    adminOnboardingPassed: true
  }
  await store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[ONBOARDING_HUB]]: newState
  })
}

const finishOnboarding = async (options = {}) => {
  if (isNavigating.value) {
    return
  }

  setNavigating(true)

  try {
    await completeOnboarding(options)
  } finally {
    setNavigating(false)
  }
}

provideOnboardingAiBillingComplete(finishOnboarding)

const goNext = async (payload = {}) => {
  if (isNavigating.value) {
    return
  }

  setNavigating(true)

  try {
    const currentStep = localData.value.currentStep
    const dataKey = stepResolver.getStepDataKey(currentStep)
    const dataTypeId = stepResolver.getStepDataTypeId(currentStep)

    if (dataKey && dataTypeId) {
      const saved = await saveData({
        dataTypeId,
        value: localData.value[dataKey]
      })

      if (!saved) {
        return
      }
    }

    const nextStep = await resolveNextStep(currentStep)

    if (!nextStep) {
      await completeOnboarding({ payload })
      return
    }

    const synced = await syncRemoteState(nextStep, { payload })

    if (!synced) {
      return
    }

    localData.value.currentStep = nextStep
  } finally {
    setNavigating(false)
  }
}

const api = new OnboardingAiApiHandler()

const resolveInitialStep = stateId => {
  if (isOnboardingStateFailed(stateId)) {
    return ONBOARDING_STEP.FAILED_LOADING
  }

  if (!toValue(stepResolver.interactiveSteps)?.includes(stateId)) {
    const mappedStateId = REDIRECT_STEP_MAPPER[stateId]
    if (!isUndefined(mappedStateId)) {
      return mappedStateId
    }
    return ONBOARDING_STEP.FAILED_LOADING
  }

  return stateId
}

const mapToLocalState = state => {
  const { data } = state
  const recommendedStructureId = getRecommendedStructureIdFromCompanySize(
    getOnboardingDataValue(data, ONBOARDING_DATA_TYPE.COMPANY_SIZE_REQUEST)
  )
  const savedSelectedStructureId = getOnboardingDataValue(data, ONBOARDING_DATA_TYPE.STRUCTURE)

  const companyWebsite = getOnboardingDataValue(data, ONBOARDING_DATA_TYPE.COMPANY_WEBSITE_URL, {
    defaultValue: ''
  })

  return {
    currentStep: resolveInitialStep(state.stateId),
    [ONBOARDING_DATA_KEY.COMPANY_WEBSITE]: resolveCompanyWebsiteFromEmail(
      companyWebsite,
      store.state.system.userData?.email
    ),
    [ONBOARDING_DATA_KEY.SELECTED_GOALS]: getOnboardingDataValue(data, ONBOARDING_DATA_TYPE.GOALS, {
      defaultValue: [],
      parseJson: true
    }),
    [ONBOARDING_DATA_KEY.RECOMMENDED_STRUCTURE_ID]: recommendedStructureId,
    [ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID]: resolveSelectedStructureId({
      savedStructureId: savedSelectedStructureId,
      recommendedStructureId
    }),
    [ONBOARDING_DATA_KEY.RESEARCH_DATA]: getOnboardingDataValue(
      data,
      ONBOARDING_DATA_TYPE.RESEARCH_REQUEST,
      { defaultValue: null, parseJson: true }
    ),
    [ONBOARDING_DATA_KEY.SELECTED_INTERVAL_ID]: getOnboardingDataValue(
      data,
      ONBOARDING_DATA_TYPE.INTERVAL,
      { defaultValue: getDefaultOnboardingAiIntervalId() }
    ),
    [ONBOARDING_DATA_KEY.RESEARCH_NOTES]: getOnboardingDataValue(
      data,
      ONBOARDING_DATA_TYPE.RESEARCH_NOTES,
      { defaultValue: '' }
    )
  }
}

const state = ref(null)
const initState = async () => {
  try {
    stepContext.loading = true
    await pullOnboardingState()
    stepContext.sessionId = state.value?.id ?? null
    localData.value = { ...localData.value, ...mapToLocalState(state.value) }
  } catch (error) {
    handleError({ error })
  } finally {
    nextTick(() => {
      stepContext.loading = false
    })
  }
}

watch(
  () => localData.value.currentStep,
  step => {
    const loadingConfig = getLoadingConfigForStep(step)

    if (loadingConfig) {
      startRequestPoll(loadingConfig.dataTypeId)
      return
    }

    clearRequestPoll()
  },
  { immediate: true }
)

onMounted(() => {
  initState()
})

onUnmounted(() => {
  clearRequestPoll()
})

const formatValue = value => {
  if (isObject(value)) {
    return JSON.stringify(value)
  }
  return value
}

const saveData = async ({ dataTypeId, value }) => {
  if (!state.value?.id) {
    return false
  }

  const okToUpdate = await isOkToUpdate()

  if (!okToUpdate) {
    return false
  }

  try {
    await api.saveOnboardingData({
      onboardingId: state.value.id,
      dataTypeId,
      value: formatValue(value)
    })
    return true
  } catch (error) {
    handleError({ error })
    return false
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/wizard-steps';
@import '~@/assets/styles/onboarding-ai-step';

.oai-Flow {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100%;
  padding: $oai-flow-padding-top 0px $oai-flow-padding-bottom;
  box-sizing: border-box;
  justify-content: flex-start;
  height: calc(100vh - #{$oai-step-header-height});
  overflow: auto;
}

.oai-Flow_Stepper {
  margin-bottom: 32px;
}

.oai-Flow_Body {
  width: 100%;
  flex: 1;
}

.slide-next-enter-active,
.slide-next-leave-active {
  transition: all 0.25s ease-out;
}

.slide-next-enter-from {
  opacity: 0;
  transform: translateX(30px);
  filter: blur(5px);
}

.slide-next-leave-to {
  opacity: 0;
  transform: translateX(-30px);
  filter: blur(5px);
}
</style>
